import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { AxiosError } from "axios"

// Codes the backend returns in `detail.code` for 409 (backend/turnus_ai/services/kb_tag_service.py).
const CONFLICT_MESSAGES: Record<string, TranslationKeys> = {
  KB_CATEGORY_EXISTS: "KNOWLEDGEBASE/CATEGORY_NAME_EXISTS_ERROR",
  KB_TAG_EXISTS: "KNOWLEDGEBASE/TAG_NAME_EXISTS_ERROR",
}

/**
 * The message to show for a failed knowledge-base category/tag write.
 *
 * Backend `detail.message` is developer prose and never shown; known cases map to our own copy,
 * everything else falls back to the caller's generic message.
 */
export function knowledgeBaseErrorKey(
  error: unknown,
  fallback: TranslationKeys,
): TranslationKeys {
  const response = (error as AxiosError<{ detail?: unknown }>)?.response
  const detail = response?.data?.detail

  if (response?.status === 409 && detail && typeof detail === "object") {
    const code = (detail as { code?: unknown }).code
    if (typeof code === "string" && code in CONFLICT_MESSAGES) {
      return CONFLICT_MESSAGES[code]
    }
  }
  if (response?.status === 422 && rejectsAName(detail)) {
    return "KNOWLEDGEBASE/NAME_INVALID_ERROR"
  }
  return fallback
}

// FastAPI validation errors: `detail` is a list of { loc: ["body", "display_name_en"], ... }.
function rejectsAName(detail: unknown): boolean {
  return (
    Array.isArray(detail) &&
    detail.some(
      (error) =>
        Array.isArray(error?.loc) &&
        error.loc.some(
          (part: unknown) =>
            typeof part === "string" && part.startsWith("display_name"),
        ),
    )
  )
}
